// src/components/Register.js
import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_API_BASE_URL ;
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

/* ── Google Identity Services loader ── */
function useGoogleAuth(onSuccess) {
  useEffect(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) return;

    const existing = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
    const init = () => {
      window.google?.accounts.id.initialize({ client_id: clientId, callback: onSuccess });
      window.google?.accounts.id.renderButton(
        document.getElementById("google-register-btn"),
        { type: "standard", theme: "filled_black", size: "large", text: "signup_with", shape: "rectangular", width: "100%" }
      );
    };
    if (existing) { init(); return; }

    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true; script.defer = true;
    script.onload = init;
    document.body.appendChild(script);
  }, [onSuccess]);
}

function Register() {
  const [form, setForm] = useState({
    firstName: "", lastName: "", email: "",
    phoneNumber: "", password: "", confirmPassword: "",
  });
  const [showPassword, setShowPw]   = useState(false);
  const [loading, setLoading]       = useState(false);
  const [googleLoading, setGoogleL] = useState(false);
  const [error, setError]           = useState("");
  const [success, setSuccess]       = useState(false);
  const navigate = useNavigate();

  const update = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  /* Password strength */
  const strength = (() => {
    const p = form.password;
    if (!p) return 0;
    let s = 0;
    if (p.length >= 8) s++;
    if (/[A-Z]/.test(p)) s++;
    if (/[0-9]/.test(p)) s++;
    if (/[^A-Za-z0-9]/.test(p)) s++;
    return s;
  })();
  const strengthLabel = ["", "Weak", "Fair", "Good", "Strong"][strength];
  const strengthColor = ["", "#e06c6c", "#e0a46c", "#6cb8e0", "#6ce083"][strength];

  /* Google callback — same endpoint as login */
  const handleGoogleResponse = useCallback(async ({ credential }) => {
    setError("");
    setGoogleL(true);
    try {
      const res = await axios.post(`/auth/google`, { token: credential }, { withCredentials: true });
      if (res.data.success) navigate(res.data.redirectTo || "/shopping-cart");
    } catch (err) {
      setError(err.response?.data?.message || "Google sign-up failed. Please try again.");
    } finally {
      setGoogleL(false);
    }
  }, [navigate]);

  useGoogleAuth(handleGoogleResponse);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (form.password !== form.confirmPassword) { setError("Passwords do not match."); return; }
    if (form.password.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (!form.phoneNumber.trim()) { setError("Phone number is required."); return; }

    setLoading(true);
    try {
      await axios.post(
        `/register`,
        {
          firstName:   form.firstName.trim(),
          lastName:    form.lastName.trim(),   // server ignores if not in schema — harmless
          email:       form.email.trim(),
          password:    form.password,
          phoneNumber: form.phoneNumber.trim(),
        },
        { withCredentials: true }
      );
      setSuccess(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      setError(err.response?.data?.message || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  /* ── Success Screen ── */
  if (success) {
    return (
      <>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,700;1,400&family=Jost:wght@300;400;500&display=swap');
          .auth-root{font-family:'Jost',sans-serif;background:#0A0A0A;min-height:100vh;display:flex;align-items:center;justify-content:center;}
          .auth-success{text-align:center;padding:3rem 2rem;animation:fadeUp 0.6s ease both;}
          @keyframes fadeUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
          .success-icon{font-size:3rem;margin-bottom:1.2rem;display:block;color:#C9A84C;}
          .success-title{font-family:'Cormorant Garamond',serif;font-size:2.4rem;color:#fff;margin-bottom:0.5rem;}
          .success-sub{font-size:0.9rem;color:rgba(255,255,255,0.45);font-weight:300;line-height:1.7;}
          .success-dot{display:inline-block;width:8px;height:8px;border-radius:50%;background:#C9A84C;animation:pulse 1s ease infinite;margin-left:4px;}
          @keyframes pulse{0%,100%{opacity:0.4;transform:scale(0.8)}50%{opacity:1;transform:scale(1)}}
        `}</style>
        <div className="auth-root">
          <div className="auth-success">
            <span className="success-icon">✦</span>
            <h2 className="success-title">Account Created</h2>
            <p className="success-sub">
              Welcome to Nairobi's Ultimate Experience<br />
              Redirecting to sign in<span className="success-dot" />
            </p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400&family=Jost:wght@300;400;500;600&display=swap');

        :root { --gold:#C9A84C; --gold-light:#E8C97A; --dark:#0A0A0A; }

        .auth-root {
          font-family:'Jost',sans-serif;
          background:var(--dark);
          min-height:100vh;
          display:flex; flex-direction:column; align-items:center; justify-content:center;
          position:relative; overflow:hidden;
          padding:6rem 1.5rem 3rem;
        }
        .auth-root::before {
          content:''; position:absolute; top:-20%; left:50%; transform:translateX(-50%);
          width:800px; height:700px;
          background:radial-gradient(ellipse,rgba(201,168,76,0.05) 0%,transparent 65%);
          pointer-events:none;
        }

        .auth-corner { position:absolute; width:80px; height:80px; pointer-events:none; }
        .auth-corner.tl { top:80px;left:24px; border-top:1px solid rgba(201,168,76,0.22); border-left:1px solid rgba(201,168,76,0.22); }
        .auth-corner.br { bottom:24px;right:24px; border-bottom:1px solid rgba(201,168,76,0.22); border-right:1px solid rgba(201,168,76,0.22); }

        .auth-card {
          position:relative; z-index:2;
          width:100%; max-width:520px;
          background:rgba(255,255,255,0.025);
          border:1px solid rgba(201,168,76,0.2);
          border-radius:4px; padding:3rem 2.5rem 2.5rem;
          animation:authFadeUp 0.7s ease both;
        }
        @keyframes authFadeUp { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
        .auth-card::before {
          content:''; position:absolute; top:0; left:10%; right:10%; height:1px;
          background:linear-gradient(90deg,transparent,var(--gold),transparent);
        }

        .auth-eyebrow { font-size:0.62rem; letter-spacing:5px; text-transform:uppercase; color:var(--gold); text-align:center; margin-bottom:0.6rem; }
        .auth-title { font-family:'Cormorant Garamond',serif; font-size:2.4rem; font-weight:700; color:#fff; text-align:center; line-height:1.1; margin-bottom:0.3rem; }
        .auth-title em { font-style:italic; color:var(--gold-light); }
        .auth-divider { width:40px; height:1px; background:var(--gold); margin:1.2rem auto 1.5rem; opacity:0.7; }
        .auth-subtitle { font-size:0.83rem; color:rgba(255,255,255,0.35); text-align:center; margin-bottom:1.5rem; font-weight:300; line-height:1.7; }

        /* Benefits */
        .auth-benefits { display:flex; justify-content:center; gap:1.5rem; margin-bottom:1.8rem; flex-wrap:wrap; }
        .auth-benefit { display:flex; align-items:center; gap:0.4rem; font-size:0.72rem; color:rgba(255,255,255,0.35); letter-spacing:0.5px; }
        .auth-benefit span { color:var(--gold); font-size:0.65rem; }

        /* Google */
        .google-btn-wrap { margin-bottom:0.5rem; display:flex; justify-content:center; }
        .google-btn-wrap > div { width:100% !important; }

        .auth-error { background:rgba(220,53,69,0.1); border:1px solid rgba(220,53,69,0.3); border-radius:3px; padding:0.75rem 1rem; font-size:0.82rem; color:#f8908a; margin-bottom:1.5rem; display:flex; align-items:center; gap:0.5rem; }

        .auth-or { display:flex; align-items:center; gap:1rem; margin:1.5rem 0; }
        .auth-or::before,.auth-or::after { content:''; flex:1; height:1px; background:rgba(255,255,255,0.07); }
        .auth-or span { font-size:0.65rem; letter-spacing:3px; text-transform:uppercase; color:rgba(255,255,255,0.2); }

        /* Two-column row */
        .auth-row { display:grid; grid-template-columns:1fr 1fr; gap:1rem; margin-bottom:1.4rem; }
        @media(max-width:480px) { .auth-row { grid-template-columns:1fr; } }
        .auth-field { margin-bottom:1.4rem; }
        .auth-field-inline { margin-bottom:0; }

        .auth-label { display:block; font-size:0.65rem; letter-spacing:3px; text-transform:uppercase; color:rgba(255,255,255,0.4); margin-bottom:0.55rem; font-weight:500; }
        .auth-input-wrap { position:relative; }
        .auth-input {
          width:100%; background:rgba(255,255,255,0.04); border:1px solid rgba(201,168,76,0.18); border-radius:3px;
          padding:0.85rem 1rem; color:#fff; font-family:'Jost',sans-serif; font-size:0.9rem; font-weight:300;
          outline:none; transition:border-color 0.25s,background 0.25s; -webkit-appearance:none;
        }
        .auth-input::placeholder { color:rgba(255,255,255,0.18); }
        .auth-input:focus { border-color:rgba(201,168,76,0.55); background:rgba(201,168,76,0.04); }
        .auth-input.has-toggle { padding-right:3.5rem; }

        .auth-toggle { position:absolute; right:1rem; top:50%; transform:translateY(-50%); background:none; border:none; color:rgba(255,255,255,0.3); cursor:pointer; padding:0; font-size:0.72rem; letter-spacing:1px; text-transform:uppercase; font-family:'Jost',sans-serif; transition:color 0.2s; }
        .auth-toggle:hover { color:var(--gold); }

        /* Strength meter */
        .strength-row { display:flex; align-items:center; gap:0.6rem; margin-top:0.5rem; }
        .strength-bars { display:flex; gap:3px; flex:1; }
        .strength-bar { height:2px; flex:1; border-radius:2px; background:rgba(255,255,255,0.08); transition:background 0.3s; }
        .strength-label { font-size:0.65rem; letter-spacing:1.5px; text-transform:uppercase; min-width:40px; text-align:right; }

        .auth-match { margin-top:0.4rem; font-size:0.7rem; letter-spacing:0.5px; min-height:1rem; }

        .auth-submit {
          width:100%; background:var(--gold); border:none; border-radius:3px; padding:0.95rem;
          color:#0A0A0A; font-family:'Jost',sans-serif; font-weight:600; font-size:0.82rem;
          letter-spacing:3px; text-transform:uppercase; cursor:pointer;
          transition:background 0.3s,transform 0.2s,opacity 0.2s; margin-top:1.8rem;
          position:relative; overflow:hidden;
        }
        .auth-submit::after { content:''; position:absolute; inset:0; background:linear-gradient(90deg,transparent,rgba(255,255,255,0.14),transparent); transform:translateX(-100%); transition:transform 0.55s; }
        .auth-submit:hover::after { transform:translateX(100%); }
        .auth-submit:hover { background:var(--gold-light); transform:translateY(-1px); }
        .auth-submit:disabled { opacity:0.55; cursor:not-allowed; transform:none; }

        .auth-terms { font-size:0.72rem; color:rgba(255,255,255,0.25); text-align:center; margin-top:1rem; line-height:1.6; }
        .auth-terms a { color:rgba(201,168,76,0.7); text-decoration:none; transition:color 0.2s; }
        .auth-terms a:hover { color:var(--gold); }

        .auth-footer { text-align:center; margin-top:1.8rem; font-size:0.8rem; color:rgba(255,255,255,0.3); }
        .auth-footer a { color:var(--gold); text-decoration:none; font-weight:500; transition:color 0.2s; }
        .auth-footer a:hover { color:var(--gold-light); }

        .auth-spinner { display:inline-block; width:14px; height:14px; border:2px solid rgba(10,10,10,0.3); border-top-color:#0A0A0A; border-radius:50%; animation:spin 0.7s linear infinite; vertical-align:middle; margin-right:0.5rem; }
        @keyframes spin { to{transform:rotate(360deg)} }
      `}</style>

      <div className="auth-root">
        <div className="auth-corner tl" />
        <div className="auth-corner br" />

        <div className="auth-card">
          <p className="auth-eyebrow">Nairobi's Ultimate Experience</p>
          <h1 className="auth-title">Join the <em>Journey</em></h1>
          <div className="auth-divider" />
          <p className="auth-subtitle">Create your account to book tours and manage your adventures</p>

          <div className="auth-benefits">
            {["Instant Booking", "Exclusive Updates", "Priority Support"].map(b => (
              <div key={b} className="auth-benefit"><span>✦</span> {b}</div>
            ))}
          </div>

          {/* ── Google Sign-Up ── */}
          <div className="google-btn-wrap">
            <div id="google-register-btn" style={{ width: "100%" }} />
          </div>

          {error && <div className="auth-error"><span>✕</span> {error}</div>}

          <div className="auth-or"><span>or</span></div>

          <form onSubmit={handleSubmit}>
            {/* Name row */}
            <div className="auth-row">
              <div className="auth-field-inline">
                <label className="auth-label" htmlFor="reg-firstName">First Name</label>
                <input id="reg-firstName" type="text" className="auth-input" placeholder="Edwin"
                  value={form.firstName} onChange={update("firstName")} required autoComplete="given-name" />
              </div>
              <div className="auth-field-inline">
                <label className="auth-label" htmlFor="reg-lastName">Last Name</label>
                <input id="reg-lastName" type="text" className="auth-input" placeholder="Obeni"
                  value={form.lastName} onChange={update("lastName")} autoComplete="family-name" />
              </div>
            </div>

            <div className="auth-field">
              <label className="auth-label" htmlFor="reg-email">Email Address</label>
              <input id="reg-email" type="email" className="auth-input" placeholder="you@example.com"
                value={form.email} onChange={update("email")} required autoComplete="email" />
            </div>

            <div className="auth-field">
              <label className="auth-label" htmlFor="reg-phone">Phone Number</label>
              <input id="reg-phone" type="tel" className="auth-input" placeholder="+254 700 000 000"
                value={form.phoneNumber} onChange={update("phoneNumber")} required autoComplete="tel" />
            </div>

            <div className="auth-field">
              <label className="auth-label" htmlFor="reg-password">Password</label>
              <div className="auth-input-wrap">
                <input id="reg-password" type={showPassword ? "text" : "password"} className="auth-input has-toggle"
                  placeholder="••••••••" value={form.password} onChange={update("password")} required autoComplete="new-password" />
                <button type="button" className="auth-toggle" onClick={() => setShowPw(v => !v)}>
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
              {form.password && (
                <div className="strength-row">
                  <div className="strength-bars">
                    {[1,2,3,4].map(i => (
                      <div key={i} className="strength-bar" style={{ background: i <= strength ? strengthColor : undefined }} />
                    ))}
                  </div>
                  <span className="strength-label" style={{ color: strengthColor }}>{strengthLabel}</span>
                </div>
              )}
            </div>

            <div className="auth-field">
              <label className="auth-label" htmlFor="reg-confirm">Confirm Password</label>
              <div className="auth-input-wrap">
                <input id="reg-confirm" type={showPassword ? "text" : "password"} className="auth-input"
                  placeholder="••••••••" value={form.confirmPassword} onChange={update("confirmPassword")} required autoComplete="new-password" />
              </div>
              {form.confirmPassword && (
                <div className="auth-match" style={{ color: form.password === form.confirmPassword ? "#6ce083" : "#e06c6c" }}>
                  {form.password === form.confirmPassword ? "✓ Passwords match" : "✕ Passwords don't match"}
                </div>
              )}
            </div>

            <button type="submit" className="auth-submit" disabled={loading || googleLoading}>
              {loading && <span className="auth-spinner" />}
              {loading ? "Creating Account…" : "Create Account"}
            </button>

            <p className="auth-terms">
              By registering you agree to our{" "}
              <Link to="/terms-of-service">Terms of Service</Link> and{" "}
              <Link to="/privacy-policy">Privacy Policy</Link>
            </p>
          </form>

          <div className="auth-footer">
            Already have an account? <Link to="/login">Sign in →</Link>
          </div>
        </div>
      </div>
    </>
  );
}

export default Register;
